import { Plus, Trash2, Check, X as XIcon, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { supabase } from '../../../../lib/supabase';
import { useCurrentCompany } from '../../../../hooks/useCurrentCompany';

interface SkillLevel {
  id: string;
  title: string;
  rank: number | null;
}

export function SkillLevelsManager() {
  const { data: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();
  const [newTitle, setNewTitle] = useState('');
  const [newRank, setNewRank] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editRank, setEditRank] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['company-skill-levels', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error: qErr } = await supabase
        .from('company_skill_levels')
        .select('id, title, rank')
        .eq('company_id', companyId)
        .order('rank', { ascending: true, nullsFirst: false });
      if (qErr) throw qErr;
      return (data || []) as SkillLevel[];
    },
    enabled: !!companyId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['company-skill-levels', companyId] });

  const create = useMutation({
    mutationFn: async ({ title, rank }: { title: string; rank: number | null }) => {
      if (!companyId) throw new Error('No company selected');
      const { error: insErr } = await supabase
        .from('company_skill_levels')
        .insert({ company_id: companyId, title, rank });
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      setNewTitle('');
      setNewRank('');
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: async ({ id, title, rank }: { id: string; title: string; rank: number | null }) => {
      const { error: updErr } = await supabase
        .from('company_skill_levels')
        .update({ title, rank })
        .eq('id', id);
      if (updErr) throw updErr;
    },
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: delErr } = await supabase.from('company_skill_levels').delete().eq('id', id);
      if (delErr) throw delErr;
    },
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });

  const handleAdd = () => {
    setError(null);
    const title = newTitle.trim();
    if (!title) return;
    create.mutate({ title, rank: newRank ? parseInt(newRank, 10) : null });
  };

  const handleDelete = (id: string, title: string) => {
    setError(null);
    if (!window.confirm(`Delete "${title}"? This will fail if any active class uses it.`)) return;
    remove.mutate(id);
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-[#1A1717] border border-[#504A4A] rounded-lg p-3 space-y-2">
        <p className="text-sm text-[#EBFFEE] font-medium">Add Skill Level</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="Title (e.g. Beginner)"
            className="flex-1 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
          />
          <input
            type="number"
            value={newRank}
            onChange={(e) => setNewRank(e.target.value)}
            placeholder="Rank"
            className="w-24 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
          />
          <button
            onClick={handleAdd}
            disabled={create.isPending || !newTitle.trim()}
            className="flex items-center gap-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
          >
            {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-400 text-sm bg-red-900/20 border border-red-600/40 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="border border-[#363232] rounded-lg overflow-hidden">
        {query.isLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 size={20} className="animate-spin text-[#A9A3A3]" />
          </div>
        ) : query.data?.length === 0 ? (
          <p className="p-4 text-sm text-[#A9A3A3] text-center">No skill levels yet.</p>
        ) : (
          query.data?.map((s) => {
            const isEditing = editingId === s.id;
            return (
              <div
                key={s.id}
                className="flex items-center justify-between px-3 py-2 border-b border-[#363232] last:border-0 gap-2"
              >
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="flex-1 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                      autoFocus
                    />
                    <input
                      type="number"
                      value={editRank}
                      onChange={(e) => setEditRank(e.target.value)}
                      placeholder="Rank"
                      className="w-20 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                    />
                    <button
                      onClick={() =>
                        update.mutate({
                          id: s.id,
                          title: editTitle.trim(),
                          rank: editRank ? parseInt(editRank, 10) : null,
                        })
                      }
                      disabled={update.isPending || !editTitle.trim()}
                      className="p-1.5 text-green-400 hover:bg-green-900/20 rounded"
                    >
                      <Check size={14} />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                    >
                      <XIcon size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0 flex items-center gap-3">
                      <span className="text-sm text-[#EBFFEE]">{s.title}</span>
                      {s.rank !== null && (
                        <span className="text-xs text-[#A9A3A3]">rank {s.rank}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingId(s.id);
                          setEditTitle(s.title);
                          setEditRank(s.rank?.toString() ?? '');
                        }}
                        className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(s.id, s.title)}
                        disabled={remove.isPending}
                        className="p-1.5 text-[#A9A3A3] hover:text-red-400 hover:bg-red-900/20 rounded disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
